import React, { useState, useMemo, useEffect, useContext } from "react";
import ReadOnlyExemptButton from "../../components/ReadOnlyExemptButton";
import { useTranslation, Trans } from "react-i18next";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Box,
  TableContainer,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  ModalCloseButton,
  HStack,
  VStack,
  ButtonGroup,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  IconButton,
  Tooltip,
  Flex,
  Spacer,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Checkbox,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { FaPlusCircle } from "react-icons/fa";
import { FaCircleMinus } from "react-icons/fa6";
import {
  EquipmentCategoryType,
  EquipmentType,
  EquipmentGroupType,
  EquipmentSupplierType,
  UniqueProcessType,
  Currency,
  Errors,
  PagePermissions,
} from '../../contexts/types';
import Decimal from 'decimal.js';
import { formatNumber, hasDecimal, isValidDateFormat, isValidDateRange } from '../../utils/utils';
import { REGEX_BRANCH, isModifiedRow, getBaseId, getBaseTotalQuantity, calculateTotalQuantity, isDisabledAddBranch, renumberDisplayOrder } from "../../utils/equipmentUtils";
import { getMultilingualDisplayName } from "../../utils/i18nDisplay";
import { CustomNumberInput } from '../InvestmentScreening/CustomNumberInput';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import GoogleAPI from "../../utils/GoogleAPI";
import { Messages, PurposeType, ValidDateRangeEnd, ValidDateRangeStart } from "../../contexts/constants";
import dayjs from "dayjs";
import EquipmentGroupSelectModal from "./EquipmentGroupSelectModal";
import EquipmentSupplierSelectModal from "./EquipmentSupplierSelectModal";
import CurrencyList from "../InvestmentScreening/CurrencyList";
import HelpIcon from "../InvestmentScreening/HelpIcon";

/**
 * モーダルのプロパティ
 */
interface Props {
  /** モーダルの表示/非表示 */
  isOpen: boolean;
  /** モーダルを閉じる関数 */
  onClose(): void;
  /** projectId */
  projectId: string;
  /** 設備データ */
  equipmentListData: EquipmentType[];
  /** 設備大分類データ */
  equipmentCategories: EquipmentCategoryType[];
  /** 設備分類データ */
  equipmentGroups: EquipmentGroupType[];
  /** サプライヤーデータ */
  equipmentSuppliers: EquipmentSupplierType[];
  /** ユニークプロセスデータ */
  uniqueProcessData: UniqueProcessType[];
  /** 通貨データ */
  currencyData: Currency[];
  /** 金額計算関数 */
  calcAmount: (
    unitPrice: number | undefined,
    quantity: number | undefined,
    equipment: EquipmentType
  ) => Decimal | undefined;
  /** 権限 */
  pagePermissions?: PagePermissions;
  /** 目的コード */
  purpose?: string;
  /** 保存完了時のコールバック */
  onSaveComplete?: () => void;
  /** 一覧ボタン押下時のコールバック */
  onListClick?: () => void;
  /** 装置・機械の仮登録コールバック（newCategoryName指定時は設備分類も仮登録） */
  onRegisterCandidate?: (name: string, nameEn: string, categoryId: string, newCategoryName?: string, newCategoryPrefix?: string, newCategoryNameEn?: string) => Promise<EquipmentGroupType | null>;
  /** サプライヤーの仮登録コールバック */
  onRegisterSupplierCandidate?: (name: string, nameEn: string) => Promise<EquipmentSupplierType | null>;
  /** 振り返り日（振り返りモード時のみ指定） */
  reviewDate?: string | null;
}

/** スタイル定義 */
const inputModalHeaderStyle: React.CSSProperties = {
  textAlign: "center",
  borderRight: "1px solid #ccc",
};

const inputModalTextStyle: React.CSSProperties = {
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
};

const inputModalButtonTextStyle: React.CSSProperties = {
  ...inputModalTextStyle,
  maxWidth: '150px',
};

/**
 * 設備編集モーダル
 */
const EquipmentEditModal: React.FC<Props> = ({
  isOpen,
  onClose,
  projectId,
  equipmentListData,
  equipmentCategories,
  equipmentGroups,
  equipmentSuppliers,
  uniqueProcessData,
  currencyData,
  calcAmount,
  pagePermissions,
  purpose,
  onSaveComplete,
  onListClick,
  onRegisterCandidate,
  onRegisterSupplierCandidate,
  reviewDate,
}) => {
  const { t } = useTranslation();
  /** 画面タイトル */
  const TITLE = t('components.EquipmentEditModal.title');

  /** スプレッドシート格納先 */
  const SPREAD_SHEET_PATH = "CEAS/SpreadSheet/EquipmentAll";

  /** 最大単価 */
  const MAX_UNIT_PRICE = 999999999999;

  /** 単価の小数点以下桁数 */
  const UNIT_PRICE_DECIMAL_PLACES = 2;

  /** 最大数量 */
  const MAX_QUANTITY = 99999;

  /** 最大人員数 */
  const MAX_NUMBER_OF_PEOPLE_REQUIRED = 999;

  /** 最大償却年数 */
  const MAX_DEPRECIATION_PERIOD = 99;

  /** 最大設備件数 */
  const MAX_EQUIPMENT_COUNT = 500;

  /** 設備件数（初期値） */
  const DEFAULT_EQUIPMENT_COUNT = 10;

  /** トースト表示用 */
  const toast = useToast();

  /** 通信オブジェクト */
  const WebAPI = useContext(WebAPIContext);

  /** Google API汎用処理クラス */
  const googleAPI: GoogleAPI = new GoogleAPI();

  /** 入力モード */
  const [inputMode, setInputMode] = useState<"google" | "manual">("manual");

  /** ローディング状態 */
  const [isLoading, setIsLoading] = useState(false);

  /** 保存中状態 */
  const [isSaving, setIsSaving] = useState(false);

  /** 設備入力件数 */
  const [equipmentCount, setEquipmentCount] = useState<number>(DEFAULT_EQUIPMENT_COUNT);

  /** 設備情報 */
  const [equipmentData, setEquipmentData] = useState<EquipmentType[]>([]);

  /** スプレッドシートウィンドウ管理 */
  const [spreadSheetWindow, setSpreadSheetWindow] = useState<Window | null>(null);

  /** スプレッドシートID */
  const [activeSpreadsheetId, setActiveSpreadsheetId] = useState<string | null>(null);

  /** 選択インデックス（設備分類・サプライヤーモーダル用） */
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  /** モーダル表示制御用：設備サプライヤー選択 */
  const [isOpenSupplierModal, setIsOpenSupplierModal] = useState(false);

  /** モーダル表示制御用：設備分類選択 */
  const [isOpenGroupModal, setIsOpenGroupModal] = useState(false);

  /** デフォルトエラー */
  const defaultErrors: Errors = {
    equipmentName: { isError: false, errorMessage: "" },
    unitPrice: { isError: false, errorMessage: "" },
    quantity: { isError: false, errorMessage: "" },
    quantityUnit: { isError: false, errorMessage: "" },
    currencyId: { isError: false, errorMessage: "" },
    depreciationPeriod: { isError: false, errorMessage: "" },
    depreciationStartMonth: { isError: false, errorMessage: "" },
    acceptanceMonth: { isError: false, errorMessage: "" },
    applicationPlannedMonth: { isError: false, errorMessage: "" },
    processId: { isError: false, errorMessage: "" },
  };

  /** エラー情報 */
  const [errorsList, setErrorsList] = useState<Errors[]>([]);

  /** 表示列の状態管理 */
  const [visibleColumns, setVisibleColumns] = useState({
    processCode: true,
    itemNo: true,
    itemName: true,
  });

  /** 全列幅定義（一箇所で管理） */
  const COLUMNS = {
    // 固定列（表示/非表示切替可能）
    button: { width: 50, toggleable: false },
    processCode: { width: 220, toggleable: true },
    itemNo: { width: 290, toggleable: true },
    itemName: { width: 220, toggleable: true },
    equipmentName: { width: 200, toggleable: false },
    // スクロール列
    unitPrice: { width: 270 },
    currency: { width: 140 },
    quantity: { width: 140 },
    quantityUnit: { width: 150 },
    amount: { width: 270 },
    bookValue: { width: 270 },
    depreciationPeriod: { width: 120 },
    applicationPlannedMonth: { width: 200 },
    acceptanceMonth: { width: 200 },
    depreciationStartMonth: { width: 200 },
    unskilledWorker: { width: 120 },
    multiSkilledWorker: { width: 120 },
    technician: { width: 120 },
    numberOfPeopleRequiredForWithoutEquipment: { width: 120 },
    equipmentGroup: { width: 220 },
    equipmentSupplier: { width: 270 },
  } as const;

  /** 列表示設定の定義 */
  const columnVisibilityOptions = useMemo(() => [
    { key: "processCode" as const, label: "ProcessCode" },
    { key: "itemNo" as const, label: "ItemNo" },
    { key: "itemName" as const, label: t('components.EquipmentEditModal.tableHeader.itemName') },
  ], [t]);

  /**
   * 固定列のleft位置を計算（表示列の状態を考慮）
   */
  const getFixedColumnLeft = (columnKey: "processCode" | "itemNo" | "itemName" | "equipmentName"): number => {
    let left = COLUMNS.button.width;
    if (columnKey === "processCode") return left;

    if (visibleColumns.processCode) left += COLUMNS.processCode.width;
    if (columnKey === "itemNo") return left;

    if (visibleColumns.itemNo) left += COLUMNS.itemNo.width;
    if (columnKey === "itemName") return left;

    if (visibleColumns.itemName) left += COLUMNS.itemName.width;
    return left;
  };

  /**
   * テーブル全体の幅を計算（表示列の状態を考慮）
   */
  const getTableWidth = (): number => {
    return Object.entries(COLUMNS).reduce((total, [key, col]) => {
      // toggleable列は表示状態をチェック
      if ('toggleable' in col && col.toggleable) {
        return total + (visibleColumns[key as keyof typeof visibleColumns] ? col.width : 0);
      }
      return total + col.width;
    }, 0);
  };

  /**
   * colgroup用の列幅配列を生成
   * ※nullは非表示列（フィルタで除外される）
   * ※配列の順序はテーブルヘッダー/ボディの列順序と一致させること
   */
  const getColumnWidths = (): (number | null)[] => [
    COLUMNS.button.width,                                              // ボタン（+/-）
    visibleColumns.processCode ? COLUMNS.processCode.width : null,     // ProcessCode
    visibleColumns.itemNo ? COLUMNS.itemNo.width : null,               // ItemNo
    visibleColumns.itemName ? COLUMNS.itemName.width : null,           // 工程名
    COLUMNS.equipmentName.width,                                       // 設備名
    COLUMNS.unitPrice.width,                                           // 単価
    COLUMNS.currency.width,                                            // 通貨
    COLUMNS.quantity.width,                                            // 数量
    COLUMNS.quantityUnit.width,                                        // 数量単位
    COLUMNS.amount.width,                                              // 投資金額
    COLUMNS.bookValue.width,                                           // 簿価
    COLUMNS.depreciationPeriod.width,                                  // 償却年数
    COLUMNS.applicationPlannedMonth.width,                             // 申請予定月
    COLUMNS.acceptanceMonth.width,                                     // 検収月
    COLUMNS.depreciationStartMonth.width,                              // 償却開始月
    COLUMNS.unskilledWorker.width,                                     // 単純工
    COLUMNS.multiSkilledWorker.width,                                  // 多能工
    COLUMNS.technician.width,                                          // テクニシャン
    COLUMNS.numberOfPeopleRequiredForWithoutEquipment.width,           // 設備なし時人員
    COLUMNS.equipmentGroup.width,                                      // 設備分類
    COLUMNS.equipmentSupplier.width,                                   // サプライヤー
  ];

  console.log("uniqueProcessData", uniqueProcessData)
  /**
   * モーダルオープン時の初期化
   */
  useEffect(() => {
    if (isOpen) {
      initializeData();
    }
  }, [isOpen, equipmentListData]);

  /**
   * データ初期化
   */
  const initializeData = () => {
    // ProcessCode順でソート
    const sortedData = sortByProcessCode([...equipmentListData]);

    // 空行を追加
    const dataWithEmptyRows = addEmptyEquipmentRows(sortedData, DEFAULT_EQUIPMENT_COUNT);

    setEquipmentData(dataWithEmptyRows);
    setEquipmentCount(Math.max(dataWithEmptyRows.length, DEFAULT_EQUIPMENT_COUNT));
    setErrorsList(dataWithEmptyRows.map(() => JSON.parse(JSON.stringify(defaultErrors))));
    setInputMode("manual");
  };

  /**
   * ProcessCode順（uniqueProcessSeq順）でソート
   */
  const sortByProcessCode = (data: EquipmentType[]): EquipmentType[] => {
    return data.sort((a, b) => {
      const aSeq = uniqueProcessData.find(p => p.uniqueProcessId === a.processId)?.uniqueProcessSeq ?? 9999;
      const bSeq = uniqueProcessData.find(p => p.uniqueProcessId === b.processId)?.uniqueProcessSeq ?? 9999;
      if (aSeq !== bSeq) return aSeq - bSeq;
      return (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
    });
  };

  /**
   * 空の設備行を追加
   */
  const addEmptyEquipmentRows = (data: EquipmentType[], targetCount: number): EquipmentType[] => {
    const result = [...data];
    while (result.length < targetCount) {
      result.push({});
    }
    return result;
  };

  /**
   * 空の設備かどうか判定
   */
  const isEmptyEquipment = (equipment: EquipmentType): boolean => {
    return !equipment.equipmentName && !equipment.unitPrice && !equipment.quantity;
  };

  /**
   * ProcessCodeプルダウンの選択肢（uniqueProcessSeq順）
   */
  const processCodeOptions = useMemo(() => {
    if (!uniqueProcessData) return [];
    return [...uniqueProcessData]
      .sort((a, b) => a.uniqueProcessSeq - b.uniqueProcessSeq)
      .map(p => ({
        value: p.uniqueProcessId ?? "",
        label: p.uniqueProcessId ?? "",
      }));
  }, [uniqueProcessData]);

  /**
   * ProcessCodeから工程名を取得（ItemNo列に表示）
   */
  const getItemNoByProcessId = (processId: string | undefined): string => {
    if (!processId || !uniqueProcessData) return "";
    const process = uniqueProcessData.find(p => p.uniqueProcessId === processId);
    return process?.uniqueProcessName ?? "";
  };

  /**
   * ProcessCodeからItemNameを取得
   */
  const getItemNameByProcessId = (processId: string | undefined): string => {
    if (!processId || !uniqueProcessData) return "";
    const process = uniqueProcessData.find(p => p.uniqueProcessId === processId);
    return process?.uniqueProcessCycleTimeInSecondByPartNoList?.[0]?.itemName ?? "";
  };

  /**
   * 行数変更
   */
  const handleEquipmentCount = (valueString: string) => {
    const count = parseInt(valueString, 10) || 0;
    if (count < 0 || count > MAX_EQUIPMENT_COUNT) return;

    if (count > equipmentData.length) {
      // 行を追加
      const newData = addEmptyEquipmentRows(equipmentData, count);
      setEquipmentData(newData);
      setErrorsList(prev => {
        const newErrors = [...prev];
        while (newErrors.length < count) {
          newErrors.push(JSON.parse(JSON.stringify(defaultErrors)));
        }
        return newErrors;
      });
    }
    setEquipmentCount(count);
  };

  /**
   * 設備情報の変更
   */
  const changeEquipment = (idx: number, propertyName: string, value: any) => {
    setEquipmentData(prevState => {
      const newState = [...prevState];
      newState[idx] = { ...newState[idx], [propertyName]: value };

      // invalidFieldsから該当フィールドを削除
      if (newState[idx].invalidFields) {
        const updatedInvalidFields = { ...newState[idx].invalidFields };
        delete updatedInvalidFields[propertyName];
        newState[idx].invalidFields = Object.keys(updatedInvalidFields).length > 0 ? updatedInvalidFields : undefined;
      }

      return newState;
    });
  };

  /**
   * 「画面上で入力」ボタン押下
   */
  const handleManualClick = () => {
    spreadSheetWindow?.close();
    setInputMode("manual");

    // スプレッドシートで空欄（""）だった行に「台（unit）」をセットし、赤枠・エラーを解除
    setEquipmentData(prevState =>
      prevState.map(row => {
        if (row.quantityUnit === "") {
          const updatedInvalidFields = { ...row.invalidFields };
          delete updatedInvalidFields["quantityUnit"];
          return {
            ...row,
            quantityUnit: "unit",
            invalidFields: Object.keys(updatedInvalidFields).length > 0 ? updatedInvalidFields : undefined,
          };
        }
        return row;
      })
    );

    // バリデーションエラーメッセージを解除
    setErrorsList(prevState =>
      prevState.map(errors => ({
        ...errors,
        quantityUnit: { isError: false, errorMessage: "" },
      }))
    );
  };

  // 固定選択肢のコードと多言語キーの対応
  const QUANTITY_UNIT_KEYS = [
    { code: "unit", key: "features.InvestmentScreening.Equipment.options.quantityUnit.unit" },
    { code: "set",  key: "features.InvestmentScreening.Equipment.options.quantityUnit.set" },
    { code: "pcs",  key: "features.InvestmentScreening.Equipment.options.quantityUnit.pcs" },
  ] as const;

  /**
   * 【出力用】DB保存値（unit/set/pcs または自由入力）をスプレッドシート表示名に変換
   */
  const toSpreadsheetUnitLabel = (code?: string | null): string => {
    const targetCode = code || "unit"; // 初期値
    const matched = QUANTITY_UNIT_KEYS.find(item => item.code === targetCode);
    // 固定選択肢に一致すれば翻訳テキスト（例: "台"）、なければ自由入力値をそのまま返す
    return matched ? t(matched.key) : targetCode;
  };

  /**
   * 【取込用】スプレッドシートのセル値（台/式/個 または自由入力）をDB保存用コードに変換
   */
  const toDbQuantityUnitCode = (cellValue?: string | null): string => {
    const val = (cellValue || "").trim();
    const matched = QUANTITY_UNIT_KEYS.find(item => t(item.key) === val || item.code.toLowerCase() === val.toLowerCase());
    // プルダウン表示名に一致すればコード（例: "unit"）、なければ自由入力値をそのまま返す
    return matched ? matched.code : (val || "unit");
  };

  /**
   * 「GoogleSpread」ボタン押下
   */
  const handleGoogleSpreadClick = async () => {
    if (inputMode === "google") return;

    try {
      setIsLoading(true);

      // ポップアップブロック回避：非同期処理より前にユーザー操作起因でウィンドウを確保
      const newWindow = window.open("", "_blank");

      // Googleアカウント認証
      if (!await googleAPI.signIn()) {
        throw new Error("signIn error");
      }

      // スプレッドシート親フォルダID取得
      // マイドライブに作成（権限制御のため共有ドライブは使用しない）
      const parentId = await googleAPI.createFolderPath(SPREAD_SHEET_PATH, null);

      // ファイル名生成
      const fileName = t('components.EquipmentEditModal.message.bulkInputFileName', { projectId });
      const spreadsheetId = await googleAPI.createSpreadSheet(fileName, parentId!, null);
      setActiveSpreadsheetId(spreadsheetId);

      // 事前確保したウィンドウをスプレッドシートに遷移
      if (newWindow) {
        newWindow.location.href = googleAPI.getOpenSpradSheetUrl(spreadsheetId);
        setSpreadSheetWindow(newWindow);

        // GoogleSpreadシートウィンドウを手動で閉じられた場合
        const interval = setInterval(() => {
          if (newWindow.closed) {
            clearInterval(interval); // 監視を停止
            setInputMode("manual");
          }
        }, 500); // 500ms ごとに監視
      }

      // ヘッダー定義
      const HEADER = [
        "ProcessCode",
        t('components.EquipmentEditModal.tableHeader.equipmentName'),
        t('components.EquipmentEditModal.tableHeader.unitPrice'),
        t('components.EquipmentEditModal.tableHeader.currency'),
        t('components.EquipmentEditModal.tableHeader.quantity'),
        t('components.EquipmentEditModal.tableHeader.quantityUnit'),
        t('components.EquipmentEditModal.tableHeader.depreciationPeriod'),
        t('components.EquipmentEditModal.spreadsheetHeader.applicationPlannedMonth'),
        t('components.EquipmentEditModal.spreadsheetHeader.acceptanceMonth'),
        t('components.EquipmentEditModal.spreadsheetHeader.depreciationStartMonth'),
        t('components.EquipmentEditModal.spreadsheetHeader.staffRequiredUnskilled'),
        t('components.EquipmentEditModal.spreadsheetHeader.staffRequiredMultiSkilled'),
        t('components.EquipmentEditModal.spreadsheetHeader.staffRequiredTechnician'),
        t('components.EquipmentEditModal.spreadsheetHeader.staffWithoutEquipment'),
        t('components.EquipmentEditModal.tableHeader.equipmentCategory'),
        t('components.EquipmentEditModal.tableHeader.supplier'),
      ];

      // 「名称 (ID)」形式の文字列を生成するヘルパー関数
      const formatNameWithId = (name?: string | null, id?: string | null): string => {
        if (!name && !id) return "";
        if (!id) return name || "";
        return `${name || ""} (${id})`;
      };

      // 画面表示中のデータを連携（新規行のみ、ProcessCode順でソート）
      const sortedData = sortByProcessCode(
        equipmentData.slice(0, equipmentCount).filter(e => !e.isReadonly && !isModifiedRow(e) && !isEmptyEquipment(e))
      );

      const values: string[][] = sortedData.map(equipment => [
        equipment.processId ?? "",
        equipment.equipmentName ?? "",
        String(equipment.unitPrice ?? ""),
        equipment.currencyName ?? "",
        String(equipment.quantity ?? ""),
        toSpreadsheetUnitLabel(equipment.quantityUnit),
        String(equipment.depreciationPeriod ?? ""),
        padYearMonth(equipment.applicationPlannedMonth ?? ""),
        padYearMonth(equipment.acceptanceMonth ?? ""),
        padYearMonth(equipment.depreciationStartMonth ?? ""),
        String(equipment.unskilledWorker ?? ""),
        String(equipment.multiSkilledWorker ?? ""),
        String(equipment.technician ?? ""),
        String(equipment.numberOfPeopleRequiredForWithoutEquipment ?? ""),
        formatNameWithId(getMultilingualDisplayName(equipment.equipmentGroupName), equipment.equipmentGroupId),
        formatNameWithId(getMultilingualDisplayName(equipment.equipmentSupplierName), equipment.equipmentSupplierId),
      ]);

      // 空行で埋める
      const emptyValues = Array.from({ length: MAX_EQUIPMENT_COUNT + 1 }, () => Array(16).fill(""));

      // シート名を取得
      const sheetName = await googleAPI.getFirstSheetName(spreadsheetId) ?? GoogleAPI.DEFAULT_SHEET_NAME;

      // スプレッドシート情報を取得してシートIDを取得
      const spreadSheetInfo = await googleAPI.getSpreadSheetInfo(spreadsheetId);
      const sheetId = spreadSheetInfo.sheets?.[0]?.properties?.sheetId ?? 0;
      // 前回設定した書式・入力規則を初期化（列構成変更時に古い設定が残らないようにする。値の書き込みより前に実行）
      await googleAPI.resetSheetFormatAndValidation(spreadsheetId, [sheetId]);

      // 日付列をテキスト形式に設定（データ書き込み前に設定）
      await setDateColumnsAsText(spreadsheetId, sheetId);

      // 既存データを初期化
      await googleAPI.spreadSheetSetValue(spreadsheetId, sheetName, 1, 1, emptyValues);

      // データを反映
      await googleAPI.spreadSheetSetValue(spreadsheetId, sheetName, 1, 1, [[...HEADER], ...values]);

      // ProcessCodeのドロップダウン選択肢を生成
      const processOptions = processCodeOptions.map(p => p.value);

      // 設備分類のドロップダウン選択肢を生成（親コンポーネントでプロジェクトスコープ済み）
      const groupOptions = equipmentGroups
        .filter(g => !g.deleted)
        .map(g => `${getMultilingualDisplayName(g.equipmentGroupName)} (${g.equipmentGroupId})`);

      // サプライヤーのドロップダウン選択肢を生成
      const supplierOptions = equipmentSuppliers
        .filter(s => !s.deleted)
        .map(s => `${getMultilingualDisplayName(s.equipmentSupplierName)} (${s.equipmentSupplierId})`);

      // 通貨のドロップダウン選択肢を生成
      const currencyNameList = currencyData
        .filter(c => c.currencyName)
        .map(c => c.currencyName!);

      // ドロップダウンリストを設定
      const validationRanges: { sheetId: number; startRow: number; endRow: number; startCol: number; endCol: number; values: string[]; strict: boolean }[] = [];

      if (processOptions.length > 0) {
        validationRanges.push({
          sheetId: sheetId,
          startRow: 2,
          endRow: MAX_EQUIPMENT_COUNT + 2,
          startCol: 1,  // A列（ProcessCode）
          endCol: 1,
          values: processOptions,
          strict: false,
        });
      }

      if (groupOptions.length > 0) {
        validationRanges.push({
          sheetId: sheetId,
          startRow: 2,
          endRow: MAX_EQUIPMENT_COUNT + 2,
          startCol: 15,  // O列（設備分類）
          endCol: 15,
          values: groupOptions,
          strict: false,
        });
      }

      if (supplierOptions.length > 0) {
        validationRanges.push({
          sheetId: sheetId,
          startRow: 2,
          endRow: MAX_EQUIPMENT_COUNT + 2,
          startCol: 16,  // P列（サプライヤー）
          endCol: 16,
          values: supplierOptions,
          strict: false,
        });
      }

      if (currencyNameList.length > 0) {
        validationRanges.push({
          sheetId: sheetId,
          startRow: 2,
          endRow: MAX_EQUIPMENT_COUNT + 2,
          startCol: 4,  // D列（通貨）
          endCol: 4,
          values: currencyNameList,
          strict: true,  // 通貨マスタ登録済みの値のみ許可
        });
      }

      const quantityUnitOptions = QUANTITY_UNIT_KEYS.map(item => t(item.key));
      if (quantityUnitOptions.length > 0) {
        validationRanges.push({
          sheetId: sheetId,
          startRow: 2,
          endRow: MAX_EQUIPMENT_COUNT + 2,
          startCol: 6,  // F列（単位）
          endCol: 6,
          values: quantityUnitOptions,
          strict: false  // 
        });
      }

      if (validationRanges.length > 0) {
        await googleAPI.spreadSheetSetDataValidation(spreadsheetId, validationRanges);
      }

      setInputMode("google");

    } catch (error) {
      console.error("Change GoogleSpread Error.", error);
      spreadSheetWindow?.close();
      setInputMode("manual");

      toast({
        title: TITLE,
        description: t('components.EquipmentEditModal.message.googleSpreadUnavailable'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 日付列をテキスト形式に設定
   */
  const setDateColumnsAsText = async (spreadsheetId: string, sheetId: number) => {
    // G, H, I列（申請予定月、検収月、償却開始月）をテキスト形式に
    const dateColumns = [8, 9, 10]; // H=8, I=9, J=10

    for (const col of dateColumns) {
      try {
        await googleAPI.spreadSheetSetCellFormat(spreadsheetId, [{
          sheetId: sheetId,
          startRow: 2,
          endRow: MAX_EQUIPMENT_COUNT + 2,
          startCol: col,
          endCol: col,
          format: {
            numberFormat: { type: "TEXT", pattern: "@" }
          }
        }]);
      } catch (e) {
        console.warn(`Failed to set text format for column ${col}`, e);
      }
    }
  };

  /**
   * 1桁月の場合0埋めを行う
   */
  const padYearMonth = (value?: string): string => {
    if (!value) return "";
    const separator = value.includes("/") ? "/" : value.includes("-") ? "-" : null;
    if (!separator) return value;

    const [year, month] = value.split(separator);
    if (!month) return value;

    const monthNum = Number(month);
    if (isNaN(monthNum)) return value;

    const paddedMonth = String(monthNum).padStart(2, "0");
    return `${year}-${paddedMonth}`;
  };

  /**
   * YYYY-MM形式のバリデーション
   */
  const isValidYearMonth = (value: string): boolean => {
    const yearMonthRegex = /^\d{4}-(0[1-9]|1[0-2])$/;
    return yearMonthRegex.test(value);
  };

  /**
   * 「データ連携」ボタン押下
   */
  const handleSyncData = async () => {
    if (!activeSpreadsheetId) {
      console.error("スプレッドシートIDがありません");
      return;
    }

    setIsLoading(true);

    try {
      // アクセストークンリフレッシュ
      if (!await googleAPI.ensureValidToken()) {
        googleAPI.signIn();
      }

      // シート名を取得
      const sheetName = await googleAPI.getFirstSheetName(activeSpreadsheetId) ?? GoogleAPI.DEFAULT_SHEET_NAME;
      const activeRange = `${sheetName}!A1:P`;

      // スプレッドシートから値を取得
      const result = await googleAPI.spreadSheetGetValues(activeSpreadsheetId, activeRange);

      const { values } = result;
      if (values) {
        // 変換して画面に反映
        const converted = convertSheetToEquipmentData(values);

        // ProcessCode順でソート
        const sortedData = sortByProcessCode(converted);

        setEquipmentData(sortedData);
        setEquipmentCount(Math.max(sortedData.length, DEFAULT_EQUIPMENT_COUNT));
        setErrorsList(sortedData.map(() => JSON.parse(JSON.stringify(defaultErrors))));

        toast({
          title: TITLE,
          description: t('components.EquipmentEditModal.message.dataLinkSuccess'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("データ連携エラー:", error);
      toast({
        title: TITLE,
        description: t('components.EquipmentEditModal.message.dataLinkFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * スプレッドシートデータを設備データに変換
   */
  const convertSheetToEquipmentData = (values: string[][]): EquipmentType[] => {
    // 読み取り専用データ+変更行を抽出（維持する）
    const fixedData = equipmentData.filter(e => e.isReadonly || isModifiedRow(e));
    const result: EquipmentType[] = [...fixedData];

    if (!values || values.length <= 1) {
      return addEmptyEquipmentRows(result, DEFAULT_EQUIPMENT_COUNT);
    }

    // ヘッダ部＋データ部を最大件数で切り出す
    const limitValues = values.slice(0, MAX_EQUIPMENT_COUNT + 1);

    // Spreadシートデータで繰り返す
    limitValues.slice(1).forEach((row) => {
      const processId = row[0] || "";
      const equipmentName = row[1] || "";
      const invalidFields: { [key: string]: string } = {};

      if (!processId || !equipmentName) {
        return;
      }

      // ProcessCodeの検証
      const process = uniqueProcessData.find(p => p.uniqueProcessId === processId);
      if (!process) {
        invalidFields["processId"] = t('components.EquipmentEditModal.validation.invalidProcessCode');
      }

      // 数値フィールドの変換
      let unitPrice = parseNumber(row[2], "unitPrice", invalidFields, MAX_UNIT_PRICE);
      let quantity = 0;
      if (purpose === PurposeType.NewProduct.code || purpose === PurposeType.IncreasedProduction.code) {
        // 新製品・増産の場合、0より大きい数値のみ
        quantity = parseNumber(row[4], "quantity", invalidFields, MAX_QUANTITY);
        if (!invalidFields["quantity"] && quantity <= 0) {
          invalidFields["quantity"] = t('components.EquipmentEditModal.validation.greaterThanZero');
          quantity = 0;
        }
      } else {
        // SpreadSheetは新規設備行のみ編集対象のため、目的に関わらず0より大きい数値のみ
        quantity = parseNumber(row[4], "quantity", invalidFields, MAX_QUANTITY);
        if (!invalidFields["quantity"] && quantity <= 0) {
          invalidFields["quantity"] = t('components.EquipmentEditModal.validation.greaterThanZero');
          quantity = 0;
        }
      }
      let depreciationPeriod = 0;
      try {
        // 償却年数
        depreciationPeriod = (Number((row[6] || "0").toString().replace(/,/g, "")));
        if (isNaN(depreciationPeriod)) {
          depreciationPeriod = 0;
          invalidFields["depreciationPeriod"] = t('components.EquipmentEditModal.validation.numericRequired');
        } else if (depreciationPeriod > MAX_DEPRECIATION_PERIOD) {
          depreciationPeriod = MAX_DEPRECIATION_PERIOD;
        } else if (depreciationPeriod < 0) {
          depreciationPeriod = 0;
        }
      } catch (e) {
        depreciationPeriod = 0;
        invalidFields["depreciationPeriod"] = t('components.EquipmentEditModal.validation.numericRequired');
      }


      let unskilledWorker = parseNumber(row[10], "unskilledWorker", invalidFields, MAX_NUMBER_OF_PEOPLE_REQUIRED);
      let multiSkilledWorker = parseNumber(row[11], "multiSkilledWorker", invalidFields, MAX_NUMBER_OF_PEOPLE_REQUIRED);
      let technician = parseNumber(row[12], "technician", invalidFields, MAX_NUMBER_OF_PEOPLE_REQUIRED);
      let numberOfPeopleRequiredForWithoutEquipment = parseNumber(row[13], "numberOfPeopleRequiredForWithoutEquipment", invalidFields, MAX_NUMBER_OF_PEOPLE_REQUIRED);

      // 日付フィールドの変換
      let applicationPlannedMonth = padYearMonth(row[7]) || "";
      let acceptanceMonth = padYearMonth(row[8]) || "";
      let depreciationStartMonth = padYearMonth(row[9]) || "";

      if (applicationPlannedMonth && !isValidYearMonth(applicationPlannedMonth)) {
        invalidFields["applicationPlannedMonth"] = t('components.EquipmentEditModal.validation.yearMonthFormat');
        applicationPlannedMonth = "";
      }
      if (acceptanceMonth && !isValidYearMonth(acceptanceMonth)) {
        invalidFields["acceptanceMonth"] = t('components.EquipmentEditModal.validation.yearMonthFormat');
        acceptanceMonth = "";
      }
      if (depreciationStartMonth && !isValidYearMonth(depreciationStartMonth)) {
        invalidFields["depreciationStartMonth"] = t('components.EquipmentEditModal.validation.yearMonthFormat');
        depreciationStartMonth = "";
      }

      // 通貨の検証
      const currencyName = row[3] || "";
      const currency = currencyData.find(c => c.currencyName === currencyName);
      if (!currency && currencyName !== "") {
        invalidFields["currencyId"] = t('components.EquipmentEditModal.validation.invalidCurrency');
      }

      // 通貨に応じた単価の小数チェック
      if (currency && unitPrice > 0 && !hasDecimal(currency.currencyName ?? "") && unitPrice % 1 !== 0) {
        invalidFields["unitPrice"] = t('components.EquipmentEditModal.validation.integerOnlyForCurrency', { currencyName: currency.currencyName });
      }

      // 設備分類・サプライヤーの抽出
      const { id: groupId, name: groupName } = extractIdFromNameWithId(row[14] || "", equipmentGroups, "equipmentGroupId", "equipmentGroupName");
      const { id: supplierId, name: supplierName } = extractIdFromNameWithId(row[15] || "", equipmentSuppliers, "equipmentSupplierId", "equipmentSupplierName");

      if (row[14] && !groupId) {
        invalidFields["equipmentGroupId"] = t('components.EquipmentEditModal.validation.invalidEquipmentCategory');
      }
      if (row[15] && !supplierId) {
        invalidFields["equipmentSupplierId"] = t('components.EquipmentEditModal.validation.invalidSupplier');
      }

      // 数量の検証
      let quantityUnit: string = row[5] || ""; 
      try {
        const cellValue = (row[5] || "").toString().trim();
        // 空欄チェック
        if (cellValue === "") {
          quantityUnit = ""; // 値を空にする
          invalidFields["quantityUnit"] = t('features.InvestmentScreening.Equipment.validation.required'); 
        } else if (!cellValue) {
          quantityUnit = ""; // 値を空にする
          invalidFields["quantityUnit"] = t('features.InvestmentScreening.Equipment.validation.required'); 
        }else {
          // 入力がある場合のみ逆変換
          quantityUnit = toDbQuantityUnitCode(cellValue);
        }
      } catch (e) {
        quantityUnit = "";
        invalidFields["quantityUnit"] = t('features.InvestmentScreening.Equipment.validation.required');
      }

      // 既存データとのマッチング（ProcessCode + 設備名）
      const existingData = equipmentData.find(e =>
        e.processId === processId &&
        e.equipmentName === equipmentName &&
        !e.isReadonly &&
        !isModifiedRow(e)
      );

      result.push({
        equipmentId: existingData?.equipmentId,
        processId: processId,
        equipmentName: equipmentName,
        unitPrice: unitPrice,
        quantity: quantity,
        quantityUnit: quantityUnit, // 数量単位
        depreciationPeriod: depreciationPeriod,
        depreciationStartMonth: depreciationStartMonth,
        applicationPlannedMonth: applicationPlannedMonth,
        acceptanceMonth: acceptanceMonth,
        unskilledWorker: unskilledWorker,
        multiSkilledWorker: multiSkilledWorker,
        technician: technician,
        numberOfPeopleRequiredForWithoutEquipment: numberOfPeopleRequiredForWithoutEquipment,
        currencyId: currency?.currencyId ?? null,
        currencyName: currency?.currencyName ?? "",
        equipmentGroupId: groupId,
        equipmentGroupName: groupName,
        equipmentSupplierId: supplierId,
        equipmentSupplierName: supplierName,
        fixedAssetCode: existingData?.fixedAssetCode ?? "",
        scheduledOrderDate: existingData?.scheduledOrderDate ?? "",
        updateDate: existingData?.updateDate,
        invalidFields: Object.keys(invalidFields).length > 0 ? invalidFields : undefined,
      });
    });

    // displayOrder再採番（processIdごと）
    renumberDisplayOrder(result, isEmptyEquipment);

    return addEmptyEquipmentRows(result, DEFAULT_EQUIPMENT_COUNT);
  };

  /**
   * 数値パース
   */
  const parseNumber = (value: string | undefined, fieldName: string, invalidFields: { [key: string]: string }, max: number): number => {
    if (!value) return 0;
    const num = Number(value.toString().replace(/,/g, ""));
    if (isNaN(num)) {
      invalidFields[fieldName] = t('components.EquipmentEditModal.validation.numericRequired');
      return 0;
    }
    if (num < 0) {
      invalidFields[fieldName] = t('components.EquipmentEditModal.validation.nonNegativeRequired');
      return 0;
    }
    if (num > max) {
      return max;
    }
    return num;
  };

  /**
   * 「名称 (ID)」形式からIDと名称を抽出
   */
  const extractIdFromNameWithId = (
    value: string,
    masterData: any[],
    idField: string,
    nameField: string
  ): { id: string | null; name: any } => {
    if (!value) return { id: null, name: "" };

    const match = value.match(/\(([^)]+)\)$/);
    if (match) {
      const id = match[1];
      const master = masterData.find(m => m[idField] === id);
      if (master) {
        return { id: master[idField], name: master[nameField] };
      }
    }
    return { id: null, name: "" };
  };

  /**
   * 枝番作成（＋ボタン）
   */
  const handleAddBranch = (equipment: EquipmentType, idx: number) => {
    if (!equipment.equipmentId) return;

    // 親が枝番の場合のみreplaceし、そうでなければそのまま使う（Equipment.tsxと同じ）
    const baseId = getBaseId(equipment.equipmentId);
    const regex = new RegExp(`^${baseId}(?:-(\\d{3}))?$`);

    // 最大枝番を取得
    const maxBrNo = equipmentData.reduce((max, item) => {
      const match = item.equipmentId?.match(regex);
      if (match) {
        const branchStr = match[1] ?? "000";
        const branchNum = parseInt(branchStr, 10);
        return Math.max(max, branchNum);
      }
      return max;
    }, 0);

    // 新しい枝番を生成
    const newEquipment: EquipmentType = JSON.parse(JSON.stringify(equipment));
    newEquipment.equipmentId = `${baseId}-${(maxBrNo + 1).toString().padStart(3, "0")}`;
    newEquipment.isReadonly = false;
    newEquipment.quantity = undefined;
    // 枝番のdisplayOrder: ベースIDの整数部 + 0.001 * 枝番番号
    newEquipment.displayOrder = Math.floor(equipment.displayOrder || 0) + ((maxBrNo + 1) * 0.001);

    // 追加位置を計算（親の次の位置）
    const insertIdx = idx + 1;

    setEquipmentData(prevState => [
      ...prevState.slice(0, insertIdx),
      newEquipment,
      ...prevState.slice(insertIdx),
    ]);

    setEquipmentCount(prev => prev + 1);

    setErrorsList(prev => {
      const newErrors = [...prev];
      newErrors.splice(insertIdx, 0, JSON.parse(JSON.stringify(defaultErrors)));
      return newErrors;
    });
  };

  /**
   * 設備削除（－ボタン）
   */
  const handleRemoveEquipment = (idx: number) => {
    setEquipmentData(prevState => [
      ...prevState.slice(0, idx),
      ...prevState.slice(idx + 1),
    ]);

    setEquipmentCount(prev => prev - 1);

    setErrorsList(prev => {
      const newErrors = [...prev];
      newErrors.splice(idx, 1);
      return newErrors;
    });
  };

  /**
   * バリデーション
   */
  const validate = (data: EquipmentType[]): boolean => {
    let isError = false;
    const newErrorsList: Errors[] = [];

    const requiredMessage = t('components.EquipmentEditModal.validation.required');
    const isValidMonthFormatMessage = t('components.EquipmentEditModal.validation.isValidMonthFormat');
    const inputMonthRangeMessage = t('components.EquipmentEditModal.validation.inputMonthRange', {
      startMonth: dayjs(ValidDateRangeStart).format(t('common.dateFormat.dayjsYearMonth')),
      endMonth: dayjs(ValidDateRangeEnd).format(t('common.dateFormat.dayjsYearMonth')),
    });

    // ProcessCode + 設備名の重複チェック用
    const nameByProcess: { [key: string]: Set<string> } = {};

    data.forEach((equipment, i) => {
      const errors: Errors = JSON.parse(JSON.stringify(defaultErrors));
      newErrorsList.push(errors);

      if (isEmptyEquipment(equipment) || equipment.isReadonly) {
        return;
      }

      // ProcessCode
      if (!equipment.processId) {
        errors.processId = { isError: true, errorMessage: t('components.EquipmentEditModal.validation.requiredField', { field: 'ProcessCode' }) };
        isError = true;
      }

      // 設備名
      if (!equipment.equipmentName) {
        errors.equipmentName = { isError: true, errorMessage: t('components.EquipmentEditModal.validation.requiredField', { field: t('components.EquipmentEditModal.tableHeader.equipmentName') }) };
        isError = true;
      } else if (!isModifiedRow(equipment)) {
        // 重複チェック
        const processId = equipment.processId ?? "";
        if (!nameByProcess[processId]) {
          nameByProcess[processId] = new Set();
        }
        if (nameByProcess[processId].has(equipment.equipmentName)) {
          errors.equipmentName = { isError: true, errorMessage: t('components.EquipmentEditModal.validation.duplicateEquipmentName') };
          isError = true;
        } else {
          nameByProcess[processId].add(equipment.equipmentName);
        }
      }

      // 単価
      if (equipment.unitPrice === undefined || equipment.unitPrice === null) {
        errors.unitPrice = { isError: true, errorMessage: t('components.EquipmentEditModal.validation.requiredField', { field: t('components.EquipmentEditModal.tableHeader.unitPrice') }) };
        isError = true;
      } else if (equipment.currencyId) {
        // 通貨に応じた小数チェック
        const currencyName = currencyData.find(c => c.currencyId === equipment.currencyId)?.currencyName ?? "";
        if (!hasDecimal(currencyName) && (equipment.unitPrice ?? 0) % 1 !== 0) {
          errors.unitPrice = { isError: true, errorMessage: t('components.EquipmentEditModal.validation.integerOnlyForCurrency', { currencyName }) };
          isError = true;
        }
      }

      // 数量
      if (equipment.quantity === undefined || equipment.quantity === null) {
        errors.quantity = { isError: true, errorMessage: t('components.EquipmentEditModal.validation.requiredField', { field: t('components.EquipmentEditModal.tableHeader.quantity') }) };
        isError = true;
      } else if (purpose === PurposeType.NewProduct.code || purpose === PurposeType.IncreasedProduction.code) {
        // 新製品・増産の場合、0より大きい数値のみ
        if ((equipment.quantity ?? 0) <= 0) {
          errors.quantity = { isError: true, errorMessage: t('components.EquipmentEditModal.validation.greaterThanZeroQuantity') };
          isError = true;
        }
      } else {
        // 合理化・更新・その他の場合、0以外を許容（マイナス値OK）
        if (equipment.quantity === 0) {
          errors.quantity = { isError: true, errorMessage: t('components.EquipmentEditModal.validation.nonZeroQuantity') };
          isError = true;
        } else if (isModifiedRow(equipment) && calculateTotalQuantity(equipment, data) < 0) {
          // 変更行: トータル数量がマイナスになる場合はエラー
          errors.quantity = { isError: true, errorMessage: t('components.EquipmentEditModal.validation.quantityBelowOriginal') };
          isError = true;
        }
      }

      // 数量単位
      if (equipment.quantityUnit === "") {
        errors.quantityUnit = {
          isError: true,
          errorMessage: t('components.EquipmentEditModal.validation.requiredField', { field: t('components.EquipmentEditModal.tableHeader.quantityUnit') }) || requiredMessage,
        };
        isError = true;
      } else if ((equipment.quantityUnit || "").toString().trim().length > 15) {
         errors.quantityUnit = {
          isError: true,
          errorMessage: t('components.EquipmentEditModal.validation.within15Chars'), 
        };
        isError = true;
      }

      // 通貨
      if (!equipment.currencyId) {
        errors.currencyId = { isError: true, errorMessage: t('components.EquipmentEditModal.validation.requiredField', { field: t('components.EquipmentEditModal.tableHeader.currency') }) };
        isError = true;
      }

      // 償却年数
      if (equipment.depreciationPeriod === undefined || equipment.depreciationPeriod === null) {
        errors.depreciationPeriod = { isError: true, errorMessage: t('components.EquipmentEditModal.validation.requiredField', { field: t('components.EquipmentEditModal.tableHeader.depreciationPeriod') }) };
        isError = true;
      }

      // 日付フィールドのバリデーション
      const validateDateField = (value: string | undefined, fieldName: string, label: string) => {
        const validDate = `${value}-01`;
        if (!value) {
          (errors as any)[fieldName] = { isError: true, errorMessage: t('components.EquipmentEditModal.validation.requiredField', { field: label }) };
          isError = true;
        } else if (!isValidDateFormat(validDate)) {
          (errors as any)[fieldName] = { isError: true, errorMessage: t('components.EquipmentEditModal.validation.isValidMonthFormatField', { field: label }) };
          isError = true;
        } else if (!isValidDateRange(new Date(validDate))) {
          (errors as any)[fieldName] = { isError: true, errorMessage: t('components.EquipmentEditModal.validation.inputMonthRangeField', { field: label, startMonth: dayjs(ValidDateRangeStart).format(t('common.dateFormat.dayjsYearMonth')), endMonth: dayjs(ValidDateRangeEnd).format(t('common.dateFormat.dayjsYearMonth')) }) };
          isError = true;
        }
      };

      validateDateField(equipment.depreciationStartMonth, "depreciationStartMonth", t('components.EquipmentEditModal.tableHeader.depreciationStartMonth'));
      validateDateField(equipment.acceptanceMonth, "acceptanceMonth", t('components.EquipmentEditModal.tableHeader.acceptanceMonth'));
      validateDateField(equipment.applicationPlannedMonth, "applicationPlannedMonth", t('components.EquipmentEditModal.tableHeader.applicationPlannedMonth'));
    });

    setErrorsList(newErrorsList);
    return !isError;
  };

  /**
   * 保存処理
   */
  const handleSave = async () => {
    setIsSaving(true);

    try {
      const tempEquipmentData = equipmentData.slice(0, equipmentCount);

      if (!validate(tempEquipmentData)) {
        toast({
          title: TITLE,
          description: t('components.EquipmentEditModal.message.inputError'),
          status: 'warning',
          duration: 2000,
          isClosable: true,
        });
        setIsSaving(false);
        return;
      }

      // displayOrder再採番（processIdごと、固定データの最大値から連番）
      renumberDisplayOrder(tempEquipmentData, isEmptyEquipment);

      // 空データ以外+読み取り専用以外を抽出
      const validData = tempEquipmentData.filter(e => !isEmptyEquipment(e) && !e.isReadonly);

      // ProcessCodeでグループ化
      const groupedByProcess: { [key: string]: EquipmentType[] } = {};
      validData.forEach(e => {
        const processId = e.processId ?? "";
        if (!groupedByProcess[processId]) {
          groupedByProcess[processId] = [];
        }
        groupedByProcess[processId].push(e);
      });

      // 現在のvalidDataのProcessCode
      const currentProcessIds = new Set(Object.keys(groupedByProcess));

      // 元データの新規設備（!isReadonly）のProcessCode
      const originalProcessIds = new Set(
        equipmentListData
          .filter(e => !e.isReadonly && e.processId)
          .map(e => e.processId as string)
      );

      // 削除されたProcessCode（元データにあって現在ない）を特定
      const deletedProcessIds = [...originalProcessIds].filter(id => !currentProcessIds.has(id));

      // API呼び出し対象: 現在のProcessCode + 削除されたProcessCode
      const targetProcessIds = [...currentProcessIds, ...deletedProcessIds];

      // ProcessCodeごとにputEquipment APIを呼び出し
      let hasError = false;
      for (const processId of targetProcessIds) {
        const equipments = groupedByProcess[processId] || []; // 削除されたProcessCodeは空配列

        const input = {
          projectId: projectId,
          processId: processId,
          equipments: equipments.map(e => {
            // トータル数量を計算
            const totalQuantity = calculateTotalQuantity(e, tempEquipmentData);
            return {
              equipmentId: e.equipmentId,
              equipmentName: e.equipmentName,
              fixedAssetCode: e.fixedAssetCode,
              scheduledOrderDate: e.scheduledOrderDate,
              unitPrice: e.unitPrice,
              quantity: e.quantity,
              totalQuantity: totalQuantity,
              quantityUnit: e.quantityUnit || "unit",
              currencyId: e.currencyId,
              amount: calcAmount(e.unitPrice, e.quantity, e)?.toFixed(2),
              numberOfPeopleRequiredForEquipment: (e.unskilledWorker || 0) + (e.multiSkilledWorker || 0) + (e.technician || 0),
              unskilledWorker: e.unskilledWorker || 0,
              multiSkilledWorker: e.multiSkilledWorker || 0,
              technician: e.technician || 0,
              numberOfPeopleRequiredForWithoutEquipment: e.numberOfPeopleRequiredForWithoutEquipment || 0,
              equipmentSupplierId: e.equipmentSupplierId,
              equipmentGroupId: e.equipmentGroupId,
              updateDate: e.updateDate,
              depreciationPeriod: e.depreciationPeriod,
              depreciationStartMonth: e.depreciationStartMonth,
              applicationPlannedMonth: e.applicationPlannedMonth,
              acceptanceMonth: e.acceptanceMonth,
              displayOrder: e.displayOrder,
            };
          }),
          ...(reviewDate && { reviewDate }),
        };

        const result = await WebAPI.request({
          query: `
            mutation putEquipment($input: ProcessEquipmentInput!) {
              putEquipment(input: $input) {status, message}
            }
          `,
          variables: { input },
        });

        const status = result?.data?.putEquipment?.status;
        if (status === 'expired') {
          toast({
            title: TITLE,
            description: Messages.expiredMessage,
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
          console.warn(`Conflict error. putEquipment. processId=${processId}`);
          return;
        } else if (status !== "success") {
          hasError = true;
          console.error(`Failed to save equipment for process ${processId}:`, result);
        }
      }

      if (hasError) {
        toast({
          title: TITLE,
          description: t('components.EquipmentEditModal.message.partialSaveFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: TITLE,
          description: t('components.EquipmentEditModal.message.saveSuccess'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        onSaveComplete?.();
        // 保存成功で閉じる場合もGoogleSpreadを閉じる（handleCloseを経由しないため）
        spreadSheetWindow?.close();
        onClose();
        onListClick?.();
      }
    } catch (error) {
      console.error("保存エラー:", error);
      toast({
        title: TITLE,
        description: t('components.EquipmentEditModal.message.saveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);

      // 数量単位列の自由入力で設定した単位の履歴初期化
      setCustomUnits({});
      setCustomUnit("");
      setTargetRowIndex(null);
      setUnitSnap([]);
    }
  };

  /**
   * 一時保存処理（設備データをJSONファイルとしてダウンロード）
   * 保存対象: equipmentData（全行）, equipmentCount
   */
  const handleTempSave = () => {
    const snapshot = { equipmentData, equipmentCount };
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const json = JSON.stringify({ screenName: "EquipmentEditModal", savedAt: new Date().toISOString(), data: snapshot }, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `EquipmentEditModal_${dateStr}.data`;
    a.click();
    URL.revokeObjectURL(url);
    toast({
      title: TITLE,
      description: t('components.EquipmentEditModal.message.tempSaved'),
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  /**
   * 一時保存読み込み処理（JSONファイルを選択して設備データを復元）
   * 復元対象: equipmentData, equipmentCount
   */
  const handleTempLoad = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".data";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const parsed = JSON.parse(ev.target?.result as string);
          if (parsed.screenName !== "EquipmentEditModal") {
            toast({ title: TITLE, description: t('components.EquipmentEditModal.message.tempLoadScreenMismatch'), status: "error", duration: 4000, isClosable: true });
            return;
          }
          const { equipmentData: savedData, equipmentCount: savedCount } = parsed.data;
          setEquipmentData(savedData);
          setEquipmentCount(savedCount);
          setErrorsList(savedData.map(() => JSON.parse(JSON.stringify(defaultErrors))));
          toast({ title: TITLE, description: t('components.EquipmentEditModal.message.tempLoaded'), status: "success", duration: 3000, isClosable: true });
        } catch {
          toast({ title: TITLE, description: t('components.EquipmentEditModal.message.tempLoadFailed'), status: "error", duration: 4000, isClosable: true });
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  /**
   * モーダルクローズ時
   */
  const handleClose = () => {
    spreadSheetWindow?.close();
    onClose();
    onListClick?.();

    // 数量単位列の自由入力で設定した単位の履歴初期化
    setCustomUnits({});
    setCustomUnit("");
    setTargetRowIndex(null);
    setUnitSnap([]);
  };

  /**
   * 設備分類選択時
   */
  const handleSelectGroup = (group: EquipmentGroupType) => {
    changeEquipment(selectedIndex, "equipmentGroupId", group.equipmentGroupId);
    changeEquipment(selectedIndex, "equipmentGroupName", group.equipmentGroupName);
    setIsOpenGroupModal(false);
  };

  /**
   * サプライヤー選択時
   */
  const handleSelectSupplier = (supplier: EquipmentSupplierType) => {
    changeEquipment(selectedIndex, "equipmentSupplierId", supplier.equipmentSupplierId);
    changeEquipment(selectedIndex, "equipmentSupplierName", supplier.equipmentSupplierName);
    setIsOpenSupplierModal(false);
  };

  /**
   * 行の背景色を取得
   */
  const getRowBackgroundColor = (equipment: EquipmentType): string => {
    if (equipment.isReadonly) return "#eee";
    if (isModifiedRow(equipment)) return "#fafffa";
    return "white";
  };

  /** *********************************************************************************************************
* 自由入力モーダル 関連
********************************************************************************************************* */
  /** 自由入力モーダルの開閉状態 */
  const [isCustomUnitModalOpen, setIsCustomUnitModalOpen] = useState(false);
  const [customUnit, setCustomUnit] = useState("");
  const [targetRowIndex, setTargetRowIndex] = useState<number | null>(null);
  const [customUnits, setCustomUnits] = useState<{ [key: number]: string[] }>({});
  const [unitSnap, setUnitSnap] = useState<EquipmentUnitSetting[]>([]);
  type EquipmentUnitSetting = {
    displayOrder: number | null;
    quantityUnit?: string | null;
  };
  /**
   * 項目選択変更ハンドラー
   */
  const handleUnitSelectChange = (rowIndex: number, newValue: any) => {
    if (!newValue) return;

    if (newValue === "custom") {
      setTargetRowIndex(rowIndex);
      setIsCustomUnitModalOpen(true);
    } else {
      if (newValue !== "unit" && newValue !== "set" && newValue !== "pcs") {
      setCustomUnits((prev) => {
        const currentList = prev[rowIndex] || [];
        if (!currentList.includes(newValue)) {
          return { ...prev, [rowIndex]: [...currentList, newValue] };
        }
        return prev;
        });
      };
      changeEquipment(rowIndex, "quantityUnit", newValue);
    }
  };

  const handleCustomUnitCancel = () => {
    setCustomUnit("");
    setTargetRowIndex(null);
    setIsCustomUnitModalOpen(false);
  };

  /**
   * 自由入力モーダルの確定ハンドラー
   */
  const handleCustomUnitConfirm = () => {
    if (targetRowIndex !== null && customUnit.trim()) {
      const trimmedUnit = customUnit.trim();

      //　固定選択肢（「台」「式」「個」等）に該当するか判定してコード化（例: "台" → "unit"）
      const resolvedUnit = toDbQuantityUnitCode(trimmedUnit);
      const standardUnits = ["unit", "set", "pcs"];
      // 配列に追加する形で保存（重複は含めない）
      if (!standardUnits.includes(resolvedUnit)) {
        setCustomUnits((prev) => {
          const currentList = prev[targetRowIndex] || [];
          const updatedList = currentList.includes(resolvedUnit)
            ? currentList
            : [...currentList, resolvedUnit];

          return {
            ...prev,
            [targetRowIndex]: updatedList,
          };
        });
      }
      changeEquipment(targetRowIndex, "quantityUnit", resolvedUnit);
      setCustomUnit("");
      setTargetRowIndex(null);
      setIsCustomUnitModalOpen(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} closeOnOverlayClick={false} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxW="1783px" minW="720px" maxH="90vh">
          <ModalHeader>
            <fieldset disabled={!pagePermissions?.isEditable}>
              <HStack spacing={4}>
                <Text>{TITLE}</Text>
                <HStack spacing={2}>
                  <Text>{t('components.EquipmentEditModal.label.rowCount')}</Text>
                  <NumberInput
                    value={equipmentCount}
                    onChange={handleEquipmentCount}
                    width="80px"
                    min={0}
                    max={MAX_EQUIPMENT_COUNT}
                    isDisabled={inputMode === "google" || !pagePermissions?.isEditable}
                  >
                    <NumberInputField placeholder={t('components.EquipmentEditModal.label.equipmentCountPlaceholder')} />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </HStack>
                <ButtonGroup spacing={4} isAttached>
                  <ReadOnlyExemptButton
                    colorScheme={inputMode === "manual" ? "green" : "gray"}
                    isLoading={isLoading}
                    onClick={handleManualClick}
                  >
                    {t('components.EquipmentEditModal.button.manualInput')}
                  </ReadOnlyExemptButton>
                  <ReadOnlyExemptButton
                    colorScheme={inputMode === "google" ? "green" : "gray"}
                    isLoading={isLoading}
                    onClick={handleGoogleSpreadClick}
                  >
                    GoogleSpread
                  </ReadOnlyExemptButton>
                </ButtonGroup>
                <Button
                  colorScheme="blue"
                  onClick={handleSyncData}
                  isDisabled={inputMode === "manual"}
                  isLoading={isLoading}
                >
                  {t('components.EquipmentEditModal.button.syncData')}
                </Button>
                {/*
                  列表示/非表示切替ドロップダウン
                  - columnVisibilityOptionsで定義された列（ProcessCode, ItemNo, 工程名）の表示切替
                  - チェックON: 列を表示、チェックOFF: 列を非表示
                  - closeOnSelect={false}: メニューを閉じずに複数選択可能
                */}
                <Menu closeOnSelect={false}>
                  <MenuButton as={Button} rightIcon={<ChevronDownIcon />} variant="outline" size="sm">
                    {t('components.EquipmentEditModal.button.displayColumns')}
                  </MenuButton>
                  <MenuList minWidth="180px">
                    {/* columnVisibilityOptionsをループして各列のチェックボックスを生成 */}
                    {columnVisibilityOptions.map((col) => (
                      <MenuItem
                        key={col.key}
                        onClick={() => {
                          // visibleColumnsの該当キーをトグル（true⇔false）
                          setVisibleColumns(prev => ({
                            ...prev,
                            [col.key]: !prev[col.key as keyof typeof prev]
                          }));
                        }}
                      >
                        <Checkbox
                          isChecked={visibleColumns[col.key as keyof typeof visibleColumns]}
                          pointerEvents="none"  // クリックイベントはMenuItemで処理
                          mr={2}
                        />
                        {col.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </HStack>
            </fieldset>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} overflow="hidden">
            <Box display="flex" h="calc(90vh - 200px)" px="10px" >
              <Box width="100%">
                <TableContainer height="100%" overflowY="auto" overflowX="auto" border="1px solid #ccc">
                  <fieldset disabled={!pagePermissions?.isEditable} style={{ minWidth: 0 }}>
                    {/*
                      テーブル設定:
                      - width: getTableWidth()で表示列に応じた動的幅を計算
                      - tableLayout: "fixed" で列幅を固定（colgroup指定が有効になる）
                      - borderCollapse: "separate" でstickyポジションが正常動作
                    */}
                    <Table variant="simple" size="sm" style={{ tableLayout: "fixed", borderCollapse: "separate" }} cellSpacing={0} className=" mm-ceas-table-extend">
                      {/*
                        colgroup: 各列の幅を定義
                        - getColumnWidths()で列幅配列を取得（非表示列はnull）
                        - filter()でnullを除外し、表示列のみの幅配列にする
                        - map()で<col>要素を動的生成
                      */}
                      <colgroup>
                        {getColumnWidths()
                          .filter((w): w is number => w !== null)  // nullを除外（非表示列）
                          .map((width, idx) => (
                            <col key={idx} width={`${width}px`} />
                          ))}
                      </colgroup>
                      <Thead position="sticky" top={0} bg="white" zIndex={10}>
                        <Tr>
                          <Th style={inputModalHeaderStyle}
                            rowSpan={2}
                            position="sticky"
                            left="0px"
                            zIndex="1"
                            bg="white"></Th>
                          {visibleColumns.processCode && (
                            <Th style={inputModalHeaderStyle}
                              position="sticky"
                              left={`${getFixedColumnLeft("processCode")}px`}
                              zIndex="2"
                              bg="white"
                              rowSpan={2}>ProcessCode</Th>
                          )}
                          {visibleColumns.itemNo && (
                            <Th style={inputModalHeaderStyle}
                              position="sticky"
                              left={`${getFixedColumnLeft("itemNo")}px`}
                              zIndex="2"
                              bg="white"
                              rowSpan={2}>ItemNo</Th>
                          )}
                          {visibleColumns.itemName && (
                            <Th style={inputModalHeaderStyle}
                              position="sticky"
                              left={`${getFixedColumnLeft("itemName")}px`}
                              zIndex="2"
                              bg="white"
                              rowSpan={2}>{t('components.EquipmentEditModal.tableHeader.itemName')}</Th>
                          )}
                          <Th style={{ ...inputModalHeaderStyle, borderRight: "1px solid #ccc" }}
                            position="sticky"
                            left={`${getFixedColumnLeft("equipmentName")}px`}
                            zIndex="3"
                            bg="white"
                            rowSpan={2}>{t('components.EquipmentEditModal.tableHeader.equipmentName')}</Th>
                          <Th style={inputModalHeaderStyle} rowSpan={2}>{t('components.EquipmentEditModal.tableHeader.unitPrice')}</Th>
                          <Th style={inputModalHeaderStyle} rowSpan={2}>
                            <Flex align="center" gap={2}>
                              {t('components.EquipmentEditModal.tableHeader.currency')}<CurrencyList currencyData={currencyData} buttonSize="xs" fontSize="10px" />
                            </Flex>
                          </Th>
                          <Th style={inputModalHeaderStyle} rowSpan={2}>{t('components.EquipmentEditModal.tableHeader.quantity')}</Th>
                          <Th style={inputModalHeaderStyle} rowSpan={2}>{t('components.EquipmentEditModal.tableHeader.quantityUnit')}</Th>
                          <Th style={inputModalHeaderStyle} rowSpan={2}>{t('components.EquipmentEditModal.tableHeader.investmentAmount')}</Th>
                          <Th style={inputModalHeaderStyle} rowSpan={2}>{t('components.EquipmentEditModal.tableHeader.bookValue')}</Th>
                          <Th style={inputModalHeaderStyle} rowSpan={2}>{t('components.EquipmentEditModal.tableHeader.depreciationPeriod')}</Th>
                          <Th style={inputModalHeaderStyle} rowSpan={2}>{t('components.EquipmentEditModal.tableHeader.applicationPlannedMonth')}</Th>
                          <Th style={inputModalHeaderStyle} rowSpan={2}>{t('components.EquipmentEditModal.tableHeader.acceptanceMonth')}</Th>
                          <Th style={inputModalHeaderStyle} rowSpan={2}>{t('components.EquipmentEditModal.tableHeader.depreciationStartMonth')}</Th>
                          <Th style={inputModalHeaderStyle} rowSpan={1} colSpan={3}>
                            <Flex align="center" justify="center" gap={1}>
                              <Text fontSize="12px">{t('components.EquipmentEditModal.tableHeader.staffRequired')}</Text>
                              <HelpIcon size="20px" placement="bottom-start">
                                <Text>
                                  {t('components.EquipmentEditModal.help.staffRequiredLine1')}<br />
                                  {t('components.EquipmentEditModal.help.staffRequiredLine2')}
                                </Text>
                              </HelpIcon>
                            </Flex>
                          </Th>
                          <Th style={inputModalHeaderStyle} rowSpan={2}>
                            <Text fontSize="12px"><Trans i18nKey="components.EquipmentEditModal.tableHeader.staffWithoutEquipment" components={[<br key="0" />]} /></Text>
                          </Th>
                          <Th style={inputModalHeaderStyle} rowSpan={2}>{t('components.EquipmentEditModal.tableHeader.equipmentCategory')}</Th>
                          <Th style={inputModalHeaderStyle} rowSpan={2}>{t('components.EquipmentEditModal.tableHeader.supplier')}</Th>
                        </Tr>
                        <Tr>
                          <Th style={inputModalHeaderStyle}><Text fontSize="12px">{t('components.EquipmentEditModal.tableHeader.unskilledWorker')}</Text></Th>
                          <Th style={inputModalHeaderStyle}><Text fontSize="12px">{t('components.EquipmentEditModal.tableHeader.multiSkilledWorker')}</Text></Th>
                          <Th style={inputModalHeaderStyle}><Text fontSize="12px">{t('components.EquipmentEditModal.tableHeader.technician')}</Text></Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {equipmentData.slice(0, equipmentCount).map((row, rowIndex) => (
                          <Tr key={rowIndex} height="60px" background={getRowBackgroundColor(row)}>
                            {/* ボタン列 */}
                            <Td p={1}
                              style={inputModalHeaderStyle}
                              background={getRowBackgroundColor(row)}
                              position="sticky"
                              left="0px"
                              zIndex="1" >
                              {row.isReadonly ? (
                                <IconButton
                                  aria-label="Add Branch"
                                  icon={<FaPlusCircle />}
                                  onClick={() => handleAddBranch(row, rowIndex)}
                                  colorScheme="blue"
                                  size="sm"
                                  isDisabled={isDisabledAddBranch(row.equipmentId!, equipmentData)}
                                  title={t('components.EquipmentEditModal.tooltip.addBranch')}
                                />
                              ) : !isEmptyEquipment(row) ? (
                                <IconButton
                                  aria-label="Remove Equipment"
                                  icon={<FaCircleMinus />}
                                  onClick={() => handleRemoveEquipment(rowIndex)}
                                  colorScheme="red"
                                  size="sm"
                                  title={t('components.EquipmentEditModal.tooltip.removeEquipment')}
                                />
                              ) : null}
                            </Td>

                            {/* ProcessCode */}
                            {visibleColumns.processCode && (
                              <Td background={getRowBackgroundColor(row)}
                                position="sticky"
                                left={`${getFixedColumnLeft("processCode")}px`}
                                zIndex="2"
                              >
                                <FormControl isInvalid={errorsList[rowIndex]?.processId?.isError}>
                                  {row.isReadonly || isModifiedRow(row) ? (
                                    <Text style={inputModalTextStyle} title={row.processId ?? ""}>{row.processId ?? ""}</Text>
                                  ) : inputMode === "manual" ? (
                                    <Select
                                      value={row.processId || ""}
                                      onChange={(e) => changeEquipment(rowIndex, "processId", e.target.value)}
                                      bg="white"
                                    >
                                      <option value="">{t('common.label.selectPlaceholder')}</option>
                                      {processCodeOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                      ))}
                                    </Select>
                                  ) : (
                                    <Text style={inputModalTextStyle} title={row.processId ?? ""}>{row.processId ?? ""}</Text>
                                  )}
                                  <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList[rowIndex]?.processId?.errorMessage}</FormErrorMessage>
                                </FormControl>
                              </Td>
                            )}

                            {/* ItemNo */}
                            {visibleColumns.itemNo && (
                              <Td background={getRowBackgroundColor(row)}
                                position="sticky"
                                left={`${getFixedColumnLeft("itemNo")}px`}
                                zIndex="2"
                              >
                                <Text style={inputModalTextStyle} title={getItemNoByProcessId(row.processId ?? "")}>{getItemNoByProcessId(row.processId ?? "")}</Text>
                              </Td>
                            )}

                            {/* 工程名 */}
                            {visibleColumns.itemName && (
                              <Td background={getRowBackgroundColor(row)}
                                position="sticky"
                                left={`${getFixedColumnLeft("itemName")}px`}
                                zIndex="2"
                              >
                                <Text style={inputModalTextStyle} title={getItemNameByProcessId(row.processId ?? "")}>{getItemNameByProcessId(row.processId ?? "")}</Text>
                              </Td>
                            )}

                            {/* 設備名 */}
                            <Td background={getRowBackgroundColor(row)}
                              style={{ borderRight: "1px solid #ccc" }}
                              position="sticky"
                              left={`${getFixedColumnLeft("equipmentName")}px`}
                              zIndex="3">
                              <FormControl isInvalid={errorsList[rowIndex]?.equipmentName?.isError}>
                                {row.isReadonly ? (
                                  <Text style={inputModalTextStyle}>{row.equipmentName ?? ""}</Text>
                                ) : inputMode === "manual" && !isModifiedRow(row) ? (
                                  <Input
                                    value={row.equipmentName ?? ""}
                                    onChange={(e) => changeEquipment(rowIndex, "equipmentName", e.target.value)}
                                    bg="white"
                                  />
                                ) : (
                                  <Text style={inputModalTextStyle}>{row.equipmentName ?? ""}</Text>
                                )}
                                <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList[rowIndex]?.equipmentName?.errorMessage}</FormErrorMessage>
                              </FormControl>
                            </Td>

                            {/* 単価 */}
                            <Tooltip label={row.invalidFields?.["unitPrice"]} isDisabled={!row.invalidFields?.["unitPrice"]} hasArrow bg="red.500" color="white">
                              <Td bg={row.invalidFields?.["unitPrice"] ? "red.200" : getRowBackgroundColor(row)}>
                                <FormControl isInvalid={errorsList[rowIndex]?.unitPrice?.isError}>
                                  {row.isReadonly ? (
                                    <Text style={inputModalTextStyle} textAlign="right">{formatNumber(row.unitPrice || 0, row.currencyName ?? "", UNIT_PRICE_DECIMAL_PLACES)}</Text>
                                  ) : inputMode === "manual" ? (
                                    <CustomNumberInput
                                      min={0}
                                      max={MAX_UNIT_PRICE}
                                      decimalPlaces={UNIT_PRICE_DECIMAL_PLACES}
                                      value={row.unitPrice ?? null}
                                      onChange={(value) => changeEquipment(rowIndex, "unitPrice", value)}
                                      bg="white"
                                    />
                                  ) : (
                                    <Text style={inputModalTextStyle} textAlign="right">{formatNumber(row.unitPrice || 0, row.currencyName ?? "", UNIT_PRICE_DECIMAL_PLACES)}</Text>
                                  )}
                                  <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList[rowIndex]?.unitPrice?.errorMessage}</FormErrorMessage>
                                </FormControl>
                              </Td>
                            </Tooltip>

                            {/* 通貨 */}
                            <Tooltip label={row.invalidFields?.["currencyId"]} isDisabled={!row.invalidFields?.["currencyId"]} hasArrow bg="red.500" color="white">
                              <Td bg={row.invalidFields?.["currencyId"] ? "red.200" : getRowBackgroundColor(row)}>
                                <FormControl isInvalid={errorsList[rowIndex]?.currencyId?.isError}>
                                  {row.isReadonly ? (
                                    <Text style={inputModalTextStyle}>{row.currencyName ?? ""}</Text>
                                  ) : inputMode === "manual" ? (
                                    <Select
                                      value={row.currencyId || ""}
                                      onChange={(e) => {
                                        const selectedCurrency = currencyData.find(c => c.currencyId === e.target.value);
                                        changeEquipment(rowIndex, "currencyId", selectedCurrency?.currencyId);
                                        changeEquipment(rowIndex, "currencyName", selectedCurrency?.currencyName);
                                      }}
                                      bg="white"
                                    >
                                      <option value=""></option>
                                      {currencyData.map(c => (
                                        <option key={c.currencyId} value={c.currencyId}>{c.currencyName}</option>
                                      ))}
                                    </Select>
                                  ) : (
                                    <Text style={inputModalTextStyle}>{row.currencyName ?? ""}</Text>
                                  )}
                                  <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList[rowIndex]?.currencyId?.errorMessage}</FormErrorMessage>
                                </FormControl>
                              </Td>
                            </Tooltip>

                            {/* 数量 */}
                            <Tooltip label={row.invalidFields?.["quantity"]} isDisabled={!row.invalidFields?.["quantity"]} hasArrow bg="red.500" color="white">
                              <Td bg={row.invalidFields?.["quantity"] ? "red.200" : getRowBackgroundColor(row)}>
                                <FormControl isInvalid={errorsList[rowIndex]?.quantity?.isError}>
                                  {row.isReadonly ? (
                                    <Text style={inputModalTextStyle} textAlign="right">{row.totalQuantity ?? row.quantity ?? ""}</Text>
                                  ) : inputMode === "manual" ? (
                                    <CustomNumberInput
                                      min={
                                        purpose === PurposeType.NewProduct.code || purpose === PurposeType.IncreasedProduction.code
                                          ? 1
                                          : isModifiedRow(row)
                                            ? -getBaseTotalQuantity(row, equipmentData)
                                            : 1
                                      }
                                      max={MAX_QUANTITY}
                                      decimalPlaces={0}
                                      value={row.quantity ?? null}
                                      onChange={(value) => changeEquipment(rowIndex, "quantity", value)}
                                      bg="white"
                                    />
                                  ) : (
                                    <Text style={inputModalTextStyle} textAlign="right">{row.quantity ?? ""}</Text>
                                  )}
                                  <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList[rowIndex]?.quantity?.errorMessage}</FormErrorMessage>
                                </FormControl>
                              </Td>
                            </Tooltip>
                            
                            {/* 数量単位 */}
                            <Tooltip label={row.invalidFields?.["quantityUnit"]} isDisabled={!row.invalidFields?.["quantityUnit"]} hasArrow bg="red.500" color="white">
                              <Td bg={row.invalidFields?.["quantityUnit"] ? "red.200" : getRowBackgroundColor(row)}>
                                <FormControl isInvalid={errorsList[rowIndex]?.quantityUnit?.isError}>
                                  {inputMode === "manual" && !row.isReadonly && !isModifiedRow(row) ? (
                                  <Select
                                    isInvalid={!!row.invalidFields?.["quantityUnit"]}
                                    value={row.quantityUnit || "unit"}
                                    onChange={(value) => {handleUnitSelectChange(rowIndex, value.target.value)}}
                                  >
                                    <option value="unit">
                                      {t('components.EquipmentEditModal.options.quantityUnit.unit')}
                                    </option>
                                    <option value="set">
                                      {t('components.EquipmentEditModal.options.quantityUnit.set')}
                                    </option>
                                    <option value="pcs">
                                      {t('components.EquipmentEditModal.options.quantityUnit.pcs')}
                                    </option>

                                    {/* 4. 自由入力されたカスタム単位の描画処理 */}
                                    {(() => {
                                      // 標準選択肢（固定値）の定義
                                      const standardUnits = ["unit", "set", "pcs"];

                                      // 重複を排除しながらカスタム選択肢を保持するSetオブジェクト
                                      const customOptions = new Set<string>();

                                      // 初期データ（DBから取得した全行）から標準以外の単位を追加
                                      unitSnap?.forEach((item) => {
                                        if (item.quantityUnit && !standardUnits.includes(item.quantityUnit)) {
                                          customOptions.add(item.quantityUnit);
                                        }
                                      });

                                      // 自由入力モーダルで追加された全行の単位を追加
                                      Object.values(customUnits || {}).flat().forEach((unit) => {
                                        if (unit && !standardUnits.includes(unit)) {
                                          customOptions.add(unit);
                                        }
                                      });

                                      // 画面上の全行（equipmentData）で現在選択されている標準以外の単位を追加
                                      equipmentData?.forEach((eq) => {
                                        if (eq.quantityUnit && !standardUnits.includes(eq.quantityUnit)) {
                                          customOptions.add(eq.quantityUnit);
                                        }
                                      });

                                      // 配列に変換して <option> を生成
                                      return Array.from(customOptions).map((unit) => (
                                        <option key={unit} value={unit}>
                                          {unit}
                                        </option>
                                      ));
                                    })()}
                                    
                                    <option value="custom">
                                      {t('components.EquipmentEditModal.options.quantityUnit.custom')}
                                    </option>
                                  </Select>) : row.quantityUnit === null || row.quantityUnit === undefined ? (
                                    // null または undefined の場合：デフォルト値（unit）を表示
                                    <Text style={inputModalTextStyle}>
                                      {t('components.EquipmentEditModal.options.quantityUnit.unit')}
                                    </Text>
                                  ) : row.quantityUnit === "" ? null : (
                                    // 値が入っている場合：その値を表示
                                    <Text style={inputModalTextStyle}>{toSpreadsheetUnitLabel(row.quantityUnit)}</Text>
                                  )}
                                  <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList[rowIndex]?.quantityUnit?.errorMessage}</FormErrorMessage>
                                </FormControl>
                              </Td>
                            </Tooltip>

                            {/* 投資金額 */}
                            <Td background={getRowBackgroundColor(row)}>
                              <Text style={inputModalTextStyle} textAlign="right">
                                {formatNumber(calcAmount(row.unitPrice, row.quantity, row) || 0, row.currencyName ?? "")}
                              </Text>
                            </Td>

                            {/* 簿価 */}
                            <Td background={getRowBackgroundColor(row)}>
                              <Text style={inputModalTextStyle} textAlign="right">
                                {formatNumber(new Decimal(row.bookValue || 0), row.currencyName ?? "")}
                              </Text>
                            </Td>

                            {/* 償却年数 */}
                            <Tooltip label={row.invalidFields?.["depreciationPeriod"]} isDisabled={!row.invalidFields?.["depreciationPeriod"]} hasArrow bg="red.500" color="white">
                              <Td bg={row.invalidFields?.["depreciationPeriod"] ? "red.200" : getRowBackgroundColor(row)}>
                                <FormControl isInvalid={errorsList[rowIndex]?.depreciationPeriod?.isError}>
                                  {row.isReadonly ? (
                                    <Text style={inputModalTextStyle} textAlign="right">{row.depreciationPeriod ?? ""}</Text>
                                  ) : inputMode === "manual" ? (
                                    <CustomNumberInput
                                      min={0}
                                      max={MAX_DEPRECIATION_PERIOD}
                                      decimalPlaces={0}
                                      value={row.depreciationPeriod ?? null}
                                      onChange={(value) => changeEquipment(rowIndex, "depreciationPeriod", value)}
                                      bg="white"
                                    />
                                  ) : (
                                    <Text style={inputModalTextStyle} textAlign="right">{row.depreciationPeriod ?? ""}</Text>
                                  )}
                                  <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList[rowIndex]?.depreciationPeriod?.errorMessage}</FormErrorMessage>
                                </FormControl>
                              </Td>
                            </Tooltip>

                            {/* 申請予定月 */}
                            <Tooltip label={row.invalidFields?.["applicationPlannedMonth"]} isDisabled={!row.invalidFields?.["applicationPlannedMonth"]} hasArrow bg="red.500" color="white">
                              <Td bg={row.invalidFields?.["applicationPlannedMonth"] ? "red.200" : getRowBackgroundColor(row)}>
                                <FormControl isInvalid={errorsList[rowIndex]?.applicationPlannedMonth?.isError}>
                                  {row.isReadonly ? (
                                    <Text style={inputModalTextStyle}>{row.applicationPlannedMonth ?? ""}</Text>
                                  ) : inputMode === "manual" ? (
                                    <Input
                                      type="month"
                                      value={row.applicationPlannedMonth ?? ""}
                                      onChange={(e) => changeEquipment(rowIndex, "applicationPlannedMonth", e.target.value)}
                                      bg="white"
                                    />
                                  ) : (
                                    <Text style={inputModalTextStyle}>{row.applicationPlannedMonth ?? ""}</Text>
                                  )}
                                  <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList[rowIndex]?.applicationPlannedMonth?.errorMessage}</FormErrorMessage>
                                </FormControl>
                              </Td>
                            </Tooltip>

                            {/* 検収月 */}
                            <Tooltip label={row.invalidFields?.["acceptanceMonth"]} isDisabled={!row.invalidFields?.["acceptanceMonth"]} hasArrow bg="red.500" color="white">
                              <Td bg={row.invalidFields?.["acceptanceMonth"] ? "red.200" : getRowBackgroundColor(row)}>
                                <FormControl isInvalid={errorsList[rowIndex]?.acceptanceMonth?.isError}>
                                  {row.isReadonly ? (
                                    <Text style={inputModalTextStyle}>{row.acceptanceMonth ?? ""}</Text>
                                  ) : inputMode === "manual" ? (
                                    <Input
                                      type="month"
                                      value={row.acceptanceMonth ?? ""}
                                      onChange={(e) => changeEquipment(rowIndex, "acceptanceMonth", e.target.value)}
                                      bg="white"
                                    />
                                  ) : (
                                    <Text style={inputModalTextStyle}>{row.acceptanceMonth ?? ""}</Text>
                                  )}
                                  <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList[rowIndex]?.acceptanceMonth?.errorMessage}</FormErrorMessage>
                                </FormControl>
                              </Td>
                            </Tooltip>

                            {/* 償却開始月 */}
                            <Tooltip label={row.invalidFields?.["depreciationStartMonth"]} isDisabled={!row.invalidFields?.["depreciationStartMonth"]} hasArrow bg="red.500" color="white">
                              <Td bg={row.invalidFields?.["depreciationStartMonth"] ? "red.200" : getRowBackgroundColor(row)}>
                                <FormControl isInvalid={errorsList[rowIndex]?.depreciationStartMonth?.isError}>
                                  {row.isReadonly ? (
                                    <Text style={inputModalTextStyle}>{row.depreciationStartMonth ?? ""}</Text>
                                  ) : inputMode === "manual" ? (
                                    <Input
                                      type="month"
                                      value={row.depreciationStartMonth ?? ""}
                                      onChange={(e) => changeEquipment(rowIndex, "depreciationStartMonth", e.target.value)}
                                      bg="white"
                                    />
                                  ) : (
                                    <Text style={inputModalTextStyle}>{row.depreciationStartMonth ?? ""}</Text>
                                  )}
                                  <FormErrorMessage whiteSpace="normal" overflowWrap="break-word">{errorsList[rowIndex]?.depreciationStartMonth?.errorMessage}</FormErrorMessage>
                                </FormControl>
                              </Td>
                            </Tooltip>

                            {/* 単純工 */}
                            <Td background={getRowBackgroundColor(row)}>
                              {row.isReadonly ? (
                                <Text style={inputModalTextStyle} textAlign="right">{row.unskilledWorker ?? ""}</Text>
                              ) : inputMode === "manual" ? (
                                <CustomNumberInput
                                  min={0}
                                  max={MAX_NUMBER_OF_PEOPLE_REQUIRED}
                                  decimalPlaces={2}
                                  value={row.unskilledWorker ?? null}
                                  onChange={(value) => changeEquipment(rowIndex, "unskilledWorker", value)}
                                  bg="white"
                                />
                              ) : (
                                <Text style={inputModalTextStyle} textAlign="right">{row.unskilledWorker ?? ""}</Text>
                              )}
                            </Td>

                            {/* 多能工 */}
                            <Td background={getRowBackgroundColor(row)}>
                              {row.isReadonly ? (
                                <Text style={inputModalTextStyle} textAlign="right">{row.multiSkilledWorker ?? ""}</Text>
                              ) : inputMode === "manual" ? (
                                <CustomNumberInput
                                  min={0}
                                  max={MAX_NUMBER_OF_PEOPLE_REQUIRED}
                                  decimalPlaces={2}
                                  value={row.multiSkilledWorker ?? null}
                                  onChange={(value) => changeEquipment(rowIndex, "multiSkilledWorker", value)}
                                  bg="white"
                                />
                              ) : (
                                <Text style={inputModalTextStyle} textAlign="right">{row.multiSkilledWorker ?? ""}</Text>
                              )}
                            </Td>

                            {/* テクニシャン */}
                            <Td background={getRowBackgroundColor(row)}>
                              {row.isReadonly ? (
                                <Text style={inputModalTextStyle} textAlign="right">{row.technician ?? ""}</Text>
                              ) : inputMode === "manual" ? (
                                <CustomNumberInput
                                  min={0}
                                  max={MAX_NUMBER_OF_PEOPLE_REQUIRED}
                                  decimalPlaces={2}
                                  value={row.technician ?? null}
                                  onChange={(value) => changeEquipment(rowIndex, "technician", value)}
                                  bg="white"
                                />
                              ) : (
                                <Text style={inputModalTextStyle} textAlign="right">{row.technician ?? ""}</Text>
                              )}
                            </Td>

                            {/* 設備なし時人員数 */}
                            <Td background={getRowBackgroundColor(row)}>
                              {row.isReadonly ? (
                                <Text style={inputModalTextStyle} textAlign="right">{row.numberOfPeopleRequiredForWithoutEquipment ?? ""}</Text>
                              ) : inputMode === "manual" ? (
                                <CustomNumberInput
                                  min={0}
                                  max={MAX_NUMBER_OF_PEOPLE_REQUIRED}
                                  decimalPlaces={2}
                                  value={row.numberOfPeopleRequiredForWithoutEquipment ?? null}
                                  onChange={(value) => changeEquipment(rowIndex, "numberOfPeopleRequiredForWithoutEquipment", value)}
                                  bg="white"
                                />
                              ) : (
                                <Text style={inputModalTextStyle} textAlign="right">{row.numberOfPeopleRequiredForWithoutEquipment ?? ""}</Text>
                              )}
                            </Td>

                            {/* 設備分類 */}
                            <Tooltip label={row.invalidFields?.["equipmentGroupId"]} isDisabled={!row.invalidFields?.["equipmentGroupId"]} hasArrow bg="red.500" color="white">
                              <Td bg={row.invalidFields?.["equipmentGroupId"] ? "red.200" : getRowBackgroundColor(row)}>
                                {row.equipmentGroupName ? (
                                  <Button
                                    variant="ghost"
                                    p={0}
                                    title={getMultilingualDisplayName(row.equipmentGroupName)}
                                    isDisabled={row.isReadonly}
                                    onClick={() => {
                                      setSelectedIndex(rowIndex);
                                      setIsOpenGroupModal(true);
                                    }}
                                  >
                                    <Text style={inputModalButtonTextStyle}>
                                      {getMultilingualDisplayName(row.equipmentGroupName)}
                                      {equipmentGroups.find(g => g.equipmentGroupId === row.equipmentGroupId)?.isCandidate && (
                                        <Box as="span" color="orange.500" fontSize="xs" ml={1}>({t('common.label.candidate')})</Box>
                                      )}
                                    </Text>
                                  </Button>
                                ) : !row.isReadonly && inputMode === "manual" ? (
                                  <Button
                                    colorScheme="blue"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedIndex(rowIndex);
                                      setIsOpenGroupModal(true);
                                    }}
                                  >
                                    {t('components.EquipmentEditModal.button.equipmentCategory')}
                                  </Button>
                                ) : null}
                              </Td>
                            </Tooltip>

                            {/* サプライヤー */}
                            <Tooltip label={row.invalidFields?.["equipmentSupplierId"]} isDisabled={!row.invalidFields?.["equipmentSupplierId"]} hasArrow bg="red.500" color="white">
                              <Td bg={row.invalidFields?.["equipmentSupplierId"] ? "red.200" : getRowBackgroundColor(row)}>
                                {row.equipmentSupplierName ? (
                                  <Button
                                    variant="ghost"
                                    p={0}
                                    title={getMultilingualDisplayName(row.equipmentSupplierName)}
                                    isDisabled={row.isReadonly}
                                    onClick={() => {
                                      setSelectedIndex(rowIndex);
                                      setIsOpenSupplierModal(true);
                                    }}
                                  >
                                    <Text style={inputModalButtonTextStyle}>
                                      {getMultilingualDisplayName(row.equipmentSupplierName)}
                                      {equipmentSuppliers.find(s => s.equipmentSupplierId === row.equipmentSupplierId)?.isCandidate && (
                                        <Box as="span" color="orange.500" fontSize="xs" ml={1}>({t('common.label.candidate')})</Box>
                                      )}
                                    </Text>
                                  </Button>
                                ) : !row.isReadonly && inputMode === "manual" ? (
                                  <Button
                                    colorScheme="teal"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedIndex(rowIndex);
                                      setIsOpenSupplierModal(true);
                                    }}
                                  >
                                    {t('components.EquipmentEditModal.button.supplier')}
                                  </Button>
                                ) : null}
                              </Td>
                            </Tooltip>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </fieldset>
                </TableContainer>
              </Box>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Spacer />
            {pagePermissions?.isEditable && (
              <>
                <Button
                  colorScheme="teal"
                  variant="outline"
                  onClick={handleTempSave}
                  mr={2}
                >
                  {t('components.EquipmentEditModal.button.tempSave')}
                </Button>
                <Button
                  colorScheme="teal"
                  variant="outline"
                  onClick={handleTempLoad}
                  mr={2}
                >
                  {t('components.EquipmentEditModal.button.tempLoad')}
                </Button>
              </>
            )}
            <Button
              colorScheme="blue"
              isDisabled={!pagePermissions?.isEditable || isSaving}
              isLoading={isSaving}
              onClick={handleSave}
              mr={3}
            >
              {t('common.button.save')}
            </Button>
            <Button onClick={handleClose}>
              {t('common.button.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 設備分類選択モーダル */}
      <EquipmentGroupSelectModal
        isOpen={isOpenGroupModal}
        onClose={() => setIsOpenGroupModal(false)}
        equipmentGroups={equipmentGroups}
        equipmentCategories={equipmentCategories}
        onSelect={handleSelectGroup}
        onRegisterCandidate={onRegisterCandidate}
      />

      {/* サプライヤー選択モーダル */}
      <EquipmentSupplierSelectModal
        isOpen={isOpenSupplierModal}
        onClose={() => setIsOpenSupplierModal(false)}
        equipmentSuppliers={equipmentSuppliers}
        onSelect={handleSelectSupplier}
        onRegisterCandidate={onRegisterSupplierCandidate}
      />

      {/* 自由入力モーダル */}
      <Modal isOpen={isCustomUnitModalOpen} onClose={handleCustomUnitCancel} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('features.InvestmentScreening.Equipment.options.customUnitModal.title')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm">{t('features.InvestmentScreening.Equipment.options.customUnitModal.unit')}</FormLabel>
                <Input
                  size="sm"
                  value={customUnit}
                  maxLength={15}
                  onChange={(e) => setCustomUnit(e.target.value)}
                  placeholder={t('features.InvestmentScreening.Equipment.options.customUnitModal.placeholder')}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              size="sm"
              mr={2}
              isDisabled={!customUnit.trim()}
              onClick={handleCustomUnitConfirm}
            >
              {t('components.RationalizationSummaryInputPopover.button.confirm')}
            </Button>
            <Button size="sm" onClick={handleCustomUnitCancel}>
              {t('components.RationalizationSummaryInputPopover.button.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default EquipmentEditModal;
